import { convertToIntakeFormAnswerDto } from 'actions/intakeForm/IntakeFormConverter';
import { PetDto, PetVaccineDto, SavePetDto, SavePetVaccineDto } from 'actions/pets/PetsDtos';
import moment from 'moment-timezone';
import { store } from 'store';
import { FiltrableOption, Option } from 'types/Generics';
import { Marketplace } from 'types/Marketplace';
import { Pet, PetHairLength, PetType, PetTypeId, PetVaccine } from 'types/Pet';
import { v4 as uuid } from 'uuid';

export const convertToPet = (dto: PetDto): Pet => {
    const masterData = store.getState().masterData.data;
    const marketplace = store.getState().marketplace.data;

    const petType = masterData.petTypes.find(pt => pt.id === dto.pet_type)! as PetType;

    const petSize = marketplace.petTypes[petType.id].sizes.find(ps => ps.id === dto.pet_size)!;

    const petBreed = masterData.petBreeds.find(breed => breed.id === dto.pet_breed);

    const petGender = masterData.petGenders.find(g => g.id === dto.pet_gender) as FiltrableOption<
        string,
        PetTypeId
    >;

    const petHairColor = masterData.petHairColors.find(
        hc => hc.id === dto.pet_hair_color
    ) as FiltrableOption<string, PetTypeId>;

    const petHairLength = marketplace.petTypes[petType.id].hairLengths.find(
        hl => hl.id === dto.pet_hair_length
    ) as PetHairLength;

    return {
        id: dto.id,
        uuid: dto.uuid || uuid(),
        name: dto.name,

        active: dto.active,
        deleted: dto.deleted,

        type: petType,
        size: petSize,
        breed: petBreed,
        gender: petGender,
        hairColor: petHairColor,
        hairLength: petHairLength,
        weight: Number(dto.pet_weight) ?? undefined,

        birthdate: dto.birthdate ? moment(dto.birthdate).tz(marketplace.timeZone) : null,

        ...(dto.image_url && {
            image: {
                src: dto.image_url
            }
        }),

        vaccines:
            dto.pet_vaccine_expirations?.map(vaccine =>
                convertToPetVaccine(vaccine, marketplace)
            ) ?? [],

        answers: []
    };
};

const convertToPetVaccine = (dto: PetVaccineDto, marketplace: Marketplace): PetVaccine => {
    return {
        id: dto.id,
        uuid: dto.uuid,
        recordUrl: dto.file_url ?? undefined,
        type: marketplace.vaccines.find(vaccine => vaccine.id === dto.vaccine_id) as Option<string>,
        expiration: dto.expiration_date
            ? moment(dto.expiration_date).tz(marketplace.timeZone)
            : undefined,
        status: dto.status
    };
};

export const convertToSavePetDto = (pet: Pet, marketplaceId: number): SavePetDto => {
    return {
        uuid: pet.uuid,
        marketplace_id: marketplaceId,
        name: pet.name,
        active: pet.active,
        deleted: pet.deleted,
        pet_type: pet.type.id,
        pet_size: pet.size.id,
        pet_gender: pet.gender.id,
        pet_weight: pet.weight ?? null,
        pet_breed: pet.breed?.id ?? null,
        pet_hair_color: pet.hairColor?.id ?? null,
        pet_hair_length: pet.hairLength?.id ?? null,
        birthdate: pet.birthdate ? pet.birthdate.toISOString() : null,

        ...(pet.image?.file && { image: pet.image.file }),
        ...(pet.image?.removed && { image: new Blob() }),

        pet_vaccine_expirations_attributes: pet.vaccines.map(vaccine =>
            convertToPetVaccineDto(vaccine)
        ),

        custom_question_responses_attributes: pet.answers?.map(convertToIntakeFormAnswerDto) || []
    };
};

export const convertToPetVaccineDto = (vaccine: PetVaccine): SavePetVaccineDto => {
    return {
        id: vaccine.id,
        uuid: vaccine.uuid,
        vaccine_id: vaccine.type?.id ?? '',
        expiration_date: vaccine.expiration?.toISOString() || '',

        ...(vaccine.recordFileToUpload && {
            file: vaccine.recordFileToUpload
        })
    };
};
